import React, { useEffect, useState, useContext } from 'react';
import { Box, Grid, Button, Container, Typography } from '@material-ui/core';
import i18n from 'i18n-js';
import Can from 'components/Can';
import Layout from 'components/Layout';
import Comments from 'components/Comments';
import useStyles from './ReservationDetails.styled';
import { withRouter, useParams } from 'react-router-dom';
import { Details, Invoice, Qualifications } from 'components/Reservations';
import { ReceivableDetails } from 'components/Receivable';
import { UserContext } from 'context';
import Logs from 'components/Logs';
import { AlarmsWidget } from 'components/Alarms';
import { FormsWidget } from 'components/Forms';
import Brokers from 'components/Brokers';
import { BaseModal } from 'components/UI';
import {
  ChangeBrokersModal,
  ChangeTimesReservationModal,
  ChangeVehicleReservationModal,
} from 'components/Modals';
import { usersActions } from 'state/modules/users';
import { useDispatch, useSelector } from 'react-redux';
import { VehicleDetailsWidget } from 'components/Vehicles';
import CheckboxField from 'components/FormFields/CheckboxField';
import { ReservationsActions } from 'state/modules/reservations';
import { Details as CustomerDetails } from 'components/Customers';

const ReservationDetails = () => {
  const classes = useStyles();
  const { id } = useParams();

  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const [statuses, setStatuses] = useState({});
  const [openEndModal, setOpenEndModal] = useState(false);
  const [openStartModal, setOpenStartModal] = useState(false);
  const [forceRefund, setForceRefund] = useState(false);
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [openChangeVehicleModal, setOpenChangeVehicleModal] = useState(false);
  const [openChangeBrokersModal, setOpenChangeBrokersModal] = useState(false);
  const [canUpdateBrokers, setCanUpdateBrokers] = useState(false);
  const [brokerItems, setBrokerItems] = useState([]);
  const [openUpdateTimesReservationModal, setOpenUpdateTimesReservationModal] = useState(false);

  const {
    reservation,
    endReservationSuccess,
    startReservationSuccess,
    cancelReservationSuccess,
    fetchedReservationSuccess,
  } = useSelector((state) => state.reservations);

  const userData = useSelector((state) => state.users?.user);
  const sharingType = useSelector(
    (state) => state.config?.settings?.core?.partnership?.sharing_type
  );

  const receivableType = useSelector((state) => state?.config?.settings?.core?.receivable?.type);

  const { orgType } = useContext(UserContext);

  const {
    settings: { core },
  } = useSelector((state) => state.config || {});

  const isOwner = orgType === 'msOwnerOrg';
  const hasPartnership = core?.partnership?.enabled || false;
  const isFFBEnabled = sharingType === 'FFB' && hasPartnership;
  const isDriverReservation = reservation?.reservation_type === 'reservation_driver';

  useEffect(() => {
    if (reservation?.user?.id) {
      dispatch(usersActions.fetchUserById(reservation?.user?.id));
    }
  }, [reservation, dispatch]);

  useEffect(() => {
    setStatuses({
      update: [
        i18n.t(`reservations_statuses.RESERVED`),
        i18n.t(`reservations_statuses.INPROGRESS`),
      ],
      update_vehicle: [
        i18n.t(`reservations_statuses.RESERVED`),
        i18n.t(`reservations_statuses.INPROGRESS`),
      ],
      cancel: [i18n.t(`reservations_statuses.RESERVED`), i18n.t(`reservations_statuses.PENDING`)],
      start: [i18n.t(`reservations_statuses.RESERVED`)],
      end: [i18n.t(`reservations_statuses.INPROGRESS`)],
    });
  }, []);

  useEffect(() => {
    if (userData && core) {
      Object.entries(core?.reservation?.brokers || {}).forEach(([key, value]) => {
        if (value?.can_assign && (userData?.brokers?.UBR || key !== 'UBR')) {
          setCanUpdateBrokers(true);
        }
      });
    }
  }, [core, userData]);

  useEffect(() => {
    if (id) {
      dispatch(ReservationsActions.fetchReservationById(id));
    }
  }, [id]); // eslint-disable-line

  useEffect(() => {
    if (canUpdateBrokers) {
      setBrokerItems([
        {
          title: i18n.t('reservations.edit_broker'),
          onClick: handleBrokersModalOpen,
          can: { action: 'update', name: 'reservation' },
        },
      ]);
    }
  }, [canUpdateBrokers]);

  useEffect(() => {
    if (
      startReservationSuccess ||
      cancelReservationSuccess ||
      endReservationSuccess ||
      fetchedReservationSuccess
    ) {
      dispatch(ReservationsActions.fetchReservationById(id));
    }
  }, [
    startReservationSuccess,
    cancelReservationSuccess,
    endReservationSuccess,
    fetchedReservationSuccess,
  ]);

  const handleOpenStartModal = () => setOpenStartModal((prevState) => !prevState);

  const handleOpenEndModal = () => setOpenEndModal((prevState) => !prevState);

  const handleOpenCancelModal = () => setOpenCancelModal((prevState) => !prevState);

  const handleOpenChangeVehicleModal = () => setOpenChangeVehicleModal((prevState) => !prevState);

  const handleOpenUpdateTimesReservationModal = () =>
    setOpenUpdateTimesReservationModal((prevState) => !prevState);

  const handleBrokersModalOpen = () => {
    setOpenChangeBrokersModal((prevState) => !prevState);
  };

  const getTimeZone = () => {
    if (reservation?.vehicle_group?.id && !isOwner) {
      return reservation.vehicle_group.time_zone;
    } else if (reservation?.vehicle_group?.parent?.id && isOwner) {
      return reservation.vehicle_group.parent.time_zone;
    }
  };

  const startReservation = () => {
    dispatch(ReservationsActions.startReservation(id));
    handleOpenStartModal();
  };

  const endReservation = () => {
    dispatch(ReservationsActions.endReservation(id));
    handleOpenEndModal();
  };

  const cancelReservation = () => {
    const payload = forceRefund ? { force_refund: true } : {};
    dispatch(ReservationsActions.cancelReservation(id, payload));
    handleOpenCancelModal();
  };

  const showReservationAction = (button) => {
    const statusString = i18n.t(`reservations_statuses.${reservation?.status}`);
    return statuses[button]?.includes(statusString);
  };

  return (
    <Layout title="Reservation">
      <Container className={classes.container}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid container direction="row" justifyContent="space-between" spacing={2}>
              <Grid item>
                <Typography variant="h6" gutterBottom>
                  <Box fontWeight="fontWeightBold" data-cy="title" fontSize={24} my={1}>
                    {i18n.t('reservations.details_title')} #{id}
                  </Box>
                </Typography>
              </Grid>
              {reservation &&
                reservation?.status &&
                !(isFFBEnabled && isOwner && isDriverReservation) && (
                  <Grid item>
                    {showReservationAction(i18n.t('reservations.action_buttons.update')) && (
                      <Can I="update_times" on="reservation">
                        <Button
                          color="primary"
                          variant="outlined"
                          className={classes.headerButton}
                          data-cy="change-reservation-times-btn"
                          onClick={handleOpenUpdateTimesReservationModal}
                        >
                          {i18n.t('reservations.edit_reservation')}
                        </Button>
                      </Can>
                    )}
                    {showReservationAction(
                      i18n.t('reservations.action_buttons.update_vehicle')
                    ) && (
                      <Can I="update_vehicle" on="reservation">
                        <Button
                          color="primary"
                          variant="outlined"
                          className={classes.headerButton}
                          data-cy="change-reservation-vehicle-btn"
                          onClick={handleOpenChangeVehicleModal}
                        >
                          {i18n.t('dashboard.noc_change_vehicle')}
                        </Button>
                      </Can>
                    )}
                    {showReservationAction(i18n.t('reservations.action_buttons.cancel')) && (
                      <Can I="cancel" on="reservation">
                        <Button
                          color="primary"
                          variant="outlined"
                          data-cy="cancel-reservation-btn"
                          onClick={handleOpenCancelModal}
                          className={classes.headerButton}
                        >
                          {i18n.t('reservations.cancel_reservation')}
                        </Button>
                      </Can>
                    )}
                    {showReservationAction(i18n.t('reservations.action_buttons.end')) && (
                      <Can I="end" on="reservation">
                        <Button
                          color="primary"
                          variant="contained"
                          data-cy="end-reservation-btn"
                          onClick={handleOpenEndModal}
                          className={classes.headerButton}
                        >
                          {i18n.t('reservations.end_reservation')}
                        </Button>
                      </Can>
                    )}
                    {showReservationAction(i18n.t('reservations.action_buttons.start')) && (
                      <Can I="start" on="reservation">
                        <Button
                          color="primary"
                          variant="contained"
                          data-cy="start-reservation-btn"
                          onClick={handleOpenStartModal}
                          className={classes.headerButton}
                        >
                          {i18n.t('reservations.start_reservation')}
                        </Button>
                      </Can>
                    )}
                  </Grid>
                )}
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={4}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Details id={parseInt(id)} timezone={getTimeZone()} />
                  </Grid>
                  <Can I="read" on="reservation">
                    <Grid item xs={12} sm={12}>
                      <Brokers
                        id={parseInt(id)}
                        type="reservation"
                        menuOptions={brokerItems}
                        brokers={reservation?.brokers || {}}
                      />
                    </Grid>
                  </Can>
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Comments type="reservation" id={parseInt(id)} />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} sm={12} md={4}>
                <Grid container spacing={2}>
                  {reservation?.user?.id && !(isFFBEnabled && isOwner && isDriverReservation) && (
                    <Can I="read" on="user">
                      <Grid item xs={12}>
                        <CustomerDetails id={parseInt(reservation?.user?.id)} />
                      </Grid>
                    </Can>
                  )}
                  <Can I="read" on="vehicle">
                    <Grid item xs={12}>
                      <VehicleDetailsWidget id={reservation?.vehicle?.id} />
                    </Grid>
                  </Can>
                  <Can I="read" a="conditional_instance">
                    <Grid item xs={12}>
                      <AlarmsWidget type="reservation" id={parseInt(id)} />
                    </Grid>
                  </Can>
                </Grid>
              </Grid>
              <Grid item xs={12} sm={12} md={4}>
                <Grid container spacing={2}>
                  {reservation?.invoice && reservation?.user?.id && (
                    <Can I="read" on="invoice">
                      <Grid item xs={12} sm={12}>
                        {receivableType === 'billing' ? (
                          <ReceivableDetails
                            id={reservation?.invoice?.id}
                            statusHide
                            receivableInfo={['from', 'to', 'source', 'created_time']}
                          />
                        ) : (
                          <Invoice id={reservation?.invoice?.id} />
                        )}
                      </Grid>
                    </Can>
                  )}
                  <Grid item xs={12}>
                    <FormsWidget type="reservation" id={parseInt(id)} />
                  </Grid>
                  <Can I="bypass" on="qualification">
                    <Grid item xs={12}>
                      <Qualifications id={id} />
                    </Grid>
                  </Can>
                  <Can I="read" on="event_log">
                    <Grid item xs={12} sm={12}>
                      {reservation?.reservation_type && (
                        <Logs
                          targetId={parseInt(id)}
                          targetType={
                            reservation?.reservation_type === 'reservation_employee'
                              ? 'ReservationEmployee'
                              : 'ReservationDriver'
                          }
                          title={i18n.t('reservations.logs')}
                          height={439}
                        />
                      )}
                    </Grid>
                  </Can>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        {openStartModal && (
          <Can I="start" on="reservation">
            <BaseModal
              size="sm"
              open={openStartModal}
              hasEnterButton
              hasCancelButton
              enterButtonLabel="Ok"
              enableCloseButton={false}
              onEnter={startReservation}
              onCancel={handleOpenStartModal}
              handleClick={handleOpenStartModal}
              title={i18n.t('reservations.start_reservation')}
            >
              {i18n.t('reservations.start_reservation_msg')}
            </BaseModal>
          </Can>
        )}
        {openEndModal && (
          <Can I="end" on="reservation">
            <BaseModal
              size="sm"
              open={openEndModal}
              hasEnterButton
              hasCancelButton
              enableCloseButton={false}
              title={i18n.t('reservations.end_reservation')}
              enterButtonLabel="Ok"
              onEnter={endReservation}
              onCancel={handleOpenEndModal}
              handleClick={handleOpenEndModal}
            >
              {i18n.t('reservations.end_reservation_msg')}
            </BaseModal>
          </Can>
        )}
        {openCancelModal && (
          <Can I="cancel" on="reservation">
            <BaseModal
              size="sm"
              open={openCancelModal}
              hasEnterButton
              hasCancelButton
              enableCloseButton={false}
              title={i18n.t('reservations.cancel_reservation')}
              enterButtonLabel="Ok"
              onEnter={cancelReservation}
              onCancel={handleOpenCancelModal}
              handleClick={handleOpenCancelModal}
            >
              <Box>
                {i18n.t('reservations.cancel_reservation_msg')}
                <CheckboxField
                  labelPlacement="end"
                  label={i18n.t('reservations.cancel_with_no_charge')}
                  form={{ setFieldValue: () => setForceRefund(!forceRefund) }}
                />
              </Box>
            </BaseModal>
          </Can>
        )}
        {openChangeVehicleModal && (
          <Can I="update_vehicle" on="reservation">
            <ChangeVehicleReservationModal
              id={id}
              open={openChangeVehicleModal}
              reservationUserId={reservation?.user?.id}
              vehicleId={reservation?.vehicle?.id}
              handleClick={handleOpenChangeVehicleModal}
              vehicleGroup={reservation?.vehicle_group}
              reservationType={reservation?.reservation_type}
            />
          </Can>
        )}
        {openUpdateTimesReservationModal && (
          <ChangeTimesReservationModal
            id={id}
            user={user}
            reservation={reservation}
            timezone={getTimeZone()}
            open={openUpdateTimesReservationModal}
            handleClick={handleOpenUpdateTimesReservationModal}
          />
        )}
        {openChangeBrokersModal && (
          <ChangeBrokersModal
            open={true}
            source="reservation"
            reservation={reservation}
            handleClick={handleBrokersModalOpen}
          />
        )}
      </Container>
    </Layout>
  );
};

export default withRouter(ReservationDetails);
